import type { WebContainer } from '@webcontainer/api';
import { workbenchStore } from '../../stores/workbench';
import { getHeadlessTemplateFiles } from '../../templates/headlessTemplate';
import { normalizeWorkbenchPath } from '../../utils/normalizeWorkbenchPath';
import { platformFileRefusal } from './platformFiles';
import { persistUserFile } from './writeFile';

/** Count disjoint occurrences (non‑overlapping, left‑to‑right). */
export function countNonOverlapping(haystack: string, needle: string): number {
  if (!needle.length) return 0;
  let count = 0;
  let from = 0;
  while (from <= haystack.length) {
    const i = haystack.indexOf(needle, from);
    if (i < 0) break;
    count++;
    from = i + needle.length;
  }
  return count;
}

function normalizeLineEndings(text: string): string {
  return text.replace(/\r\n/g, '\n');
}

function findManualEditConflict(resolvedPath: string): { path: string; userContent: string } | null {
  const entry = workbenchStore.files.get()[resolvedPath];
  if (entry?.modified) {
    return { path: resolvedPath, userContent: entry.content };
  }
  return null;
}

/** Apply substring replacement on an existing WebContainer/store file (edit‑turn surgical tool path). */
export async function executeSearchReplace(
  webcontainer: WebContainer | null,
  filePath: string,
  oldString: string,
  newString: string,
  replaceAll = false,
  signal?: AbortSignal,
): Promise<string> {
  const trimmed = normalizeWorkbenchPath(filePath);
  if (!trimmed) {
    return 'Error: invalid path';
  }

  const refusal = platformFileRefusal(trimmed, workbenchStore.files.get());
  if (refusal) return refusal;

  let current: string | undefined = workbenchStore.files.get()[trimmed]?.content;
  if (current === undefined && webcontainer) {
    try {
      current = await webcontainer.fs.readFile(trimmed, 'utf-8');
    } catch {
      current = undefined;
    }
  }
  if (current === undefined) {
    // A file the store does not track is a template file: in the light preview that is where
    // the workspace's untouched files live, and with a container it is what was mounted.
    current = getHeadlessTemplateFiles()[trimmed];
  }
  if (current === undefined) {
    return `Error: file not found: ${trimmed}`;
  }

  const normalizedCurrent = normalizeLineEndings(current);
  const normalizedOld = normalizeLineEndings(oldString);
  const normalizedNew = normalizeLineEndings(newString);

  const occurrences = countNonOverlapping(normalizedCurrent, normalizedOld);
  if (occurrences === 0) {
    return `Error: oldString not found in ${trimmed}`;
  }

  let next = normalizedCurrent;

  if (replaceAll) {
    next = normalizedCurrent.split(normalizedOld).join(normalizedNew);
  } else if (occurrences !== 1) {
    return `Error: expected exactly one occurrence of oldString in ${trimmed}, found ${occurrences}`;
  } else {
    next = normalizedCurrent.replace(normalizedOld, normalizedNew);
  }

  const manualConflict = findManualEditConflict(trimmed);
  if (manualConflict) {
    workbenchStore.setFileConflict(trimmed, {
      aiContent: next,
      userContent: manualConflict.userContent,
    });
    return (
      `Conflict: ${trimmed} has manual edits — kept your version. ` +
      `Review the diff in the editor and accept or reject the AI changes.`
    );
  }

  if (signal?.aborted) {
    return `Skipped: turn was cancelled before ${trimmed} could be updated.`;
  }

  await persistUserFile(webcontainer, trimmed, next, false, signal);
  workbenchStore.recordToolWriteSnapshot(trimmed, next);

  const replacedSpans = replaceAll ? occurrences : 1;
  return `Replaced ${replacedSpans} occurrence(s) in ${trimmed}`;
}
